import React, { useEffect, useRef, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate } from "react-router-dom";
import "./addPropertyResaleVibe.css";
import { addPropertyResaleVibe } from "../../Redux/Actions/propertyResaleVibeActions";
import { useDispatch, useSelector } from "react-redux";
import { viewCategoryList, viewSubCategoryList } from "../../Redux/Actions/categoryResaleActions";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";

const TYPE_OPTIONS = [
  { value: "business", label: "Business", emoji: "💼" },
  { value: "resale", label: "Resale", emoji: "🔄" },
];

const AddPropertyResaleVibe = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const iconInputRef = useRef(null);

  const { categories, subCategories } = useSelector((state) => state.categoriesResale || {});
  const { loading = false } = useSelector((state) => state.propertyResaleVibe || {});

  const [activeSection, setActiveSection] = useState(0);
  const [errors, setErrors] = useState({});
  const [iconPreview, setIconPreview] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconDragOver, setIconDragOver] = useState(false);

  const [vibeData, setVibeData] = useState({
    category_id: "",
    sub_category: "",
    type: [],
    name: "",
    icon: "",
  });

  useEffect(() => {
    dispatch(viewCategoryList({ page: 1, limit: 100 }));
  }, [dispatch]);

  const clearError = (field) =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const handleAddChange = (event) => {
    const { name, value } = event.target;

    if (name === "category_id") {
      setVibeData((prev) => ({
        ...prev,
        category_id: value,
        sub_category: "",
      }));

      if (value) {
        dispatch(viewSubCategoryList({ page: 1, limit: 100, category_id: value }));
      }
    } else {
      setVibeData((prev) => ({ ...prev, [name]: value }));
    }

    clearError(name);
  };

  const toggleType = (value) => {
    setVibeData((prev) => ({
      ...prev,
      type: prev.type.includes(value)
        ? prev.type.filter((t) => t !== value)
        : [...prev.type, value],
    }));
    clearError("type");
  };

  const processIconFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const preview = URL.createObjectURL(file);
    setIconPreview(preview);

    try {
      setUploadingIcon(true);
      const url = await AddImage(file);
      setVibeData((prev) => ({ ...prev, icon: url }));
      clearError("icon");
    } catch {
      toast.error("Icon upload failed");
      setIconPreview(null);
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleIconFileChange = (e) => processIconFile(e.target.files?.[0]);

  const handleIconDrop = (e) => {
    e.preventDefault();
    setIconDragOver(false);
    processIconFile(e.dataTransfer.files?.[0]);
  };

  const removeIcon = () => {
    setIconPreview(null);
    setVibeData((prev) => ({ ...prev, icon: "" }));
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const validate = () => {
    const errs = {};
    if (!vibeData.name.trim()) errs.name = "Vibe name is required";
    if (!vibeData.category_id) errs.category_id = "Category is required";
    if (!vibeData.sub_category) errs.sub_category = "Sub-category is required";
    if (!vibeData.type.length) errs.type = "Select at least one type";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        category: vibeData.category_id,
        category_id: vibeData.sub_category,
        type: vibeData.type,
        name: vibeData.name,
        icon: vibeData.icon,
      };

      const response = await dispatch(addPropertyResaleVibe(payload));

      if (response?.statusCode === 200) {
        handleReset();
        navigate("/resale_vibe");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to add property resale vibe");
    }
  };

  const handleReset = () => {
    setVibeData({
      category_id: "",
      sub_category: "",
      type: [],
      name: "",
      icon: "",
    });
    setActiveSection(0);
    setErrors({});
    setIconPreview(null);
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const sections = ["Basic Info"];

  return (
    <MainLayout>
      <div className="prv-wrapper">
        <div className="prv-header">
          <div className="prv-header-left">
            <div className="prv-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-3 3-3-3" />
              </svg>
            </div>
            <div>
              <h1 className="prv-title">Add Property Resale Vibe</h1>
              <p className="prv-subtitle">Create a new resale vibe configuration</p>
            </div>
          </div>
          <Link to="/resale_vibe" className="prv-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View Vibes
          </Link>
        </div>

        <div className="prv-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`prv-nav-pill ${activeSection === i ? "prv-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
              type="button"
              disabled={loading}
            >
              <span className="prv-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <form className="prv-form" onSubmit={handleSubmit}>
          <div className={`prv-section ${activeSection === 0 ? "prv-section--visible" : ""}`}>
            <div className="prv-section-header">
              <span className="prv-section-badge">01</span>
              <h2 className="prv-section-title">Basic Information</h2>
            </div>

            <div className="prv-grid prv-grid-2">
              <div className="prv-field">
                <label className="prv-label">
                  Category <span className="prv-req">*</span>
                </label>
                <select
                  className={`prv-select ${errors.category_id ? "prv-input--error" : ""}`}
                  name="category_id"
                  value={vibeData.category_id}
                  onChange={handleAddChange}
                  disabled={loading}
                >
                  <option value="">Select category</option>
                  {categories?.data?.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && <span className="prv-error-msg">{errors.category_id}</span>}
              </div>

              <div className="prv-field">
                <label className="prv-label">
                  Sub Category <span className="prv-req">*</span>
                </label>
                <select
                  className={`prv-select ${!vibeData.category_id ? "prv-select--disabled" : ""} ${errors.sub_category ? "prv-input--error" : ""}`}
                  name="sub_category"
                  value={vibeData.sub_category}
                  onChange={handleAddChange}
                  disabled={!vibeData.category_id || loading}
                >
                  <option value="">
                    {!vibeData.category_id ? "Select category first" : "Select sub category"}
                  </option>
                  {subCategories?.data?.map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
                {errors.sub_category && <span className="prv-error-msg">{errors.sub_category}</span>}
              </div>

              <div className="prv-field">
                <label className="prv-label">
                  Type <span className="prv-req">*</span>
                </label>
                <div className={`prv-type-toggle-group ${errors.type ? "prv-type-toggle-group--error" : ""}`}>
                  {TYPE_OPTIONS.map((opt) => {
                    const active = vibeData.type.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`prv-type-chip ${active ? "prv-type-chip--active" : ""}`}
                        onClick={() => toggleType(opt.value)}
                        disabled={loading}
                        aria-pressed={active}
                      >
                        <span className="prv-type-chip-emoji">{opt.emoji}</span>
                        <span className="prv-type-chip-label">{opt.label}</span>
                        {active && (
                          <span className="prv-type-chip-check">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {vibeData.type.length > 0 && (
                  <div className="prv-type-selected-hint">
                    Selected: {vibeData.type.map((t) => TYPE_OPTIONS.find((o) => o.value === t)?.label).join(", ")}
                  </div>
                )}
                {errors.type && <span className="prv-error-msg">{errors.type}</span>}
              </div>

              <div className="prv-field">
                <label className="prv-label">
                  Vibe Name <span className="prv-req">*</span>
                </label>
                <input
                  className={`prv-input ${errors.name ? "prv-input--error" : ""}`}
                  type="text"
                  name="name"
                  value={vibeData.name}
                  onChange={handleAddChange}
                  placeholder="e.g. Contemporary Urban Living"
                  disabled={loading}
                />
                {errors.name && <span className="prv-error-msg">{errors.name}</span>}
              </div>

              <div className="prv-field prv-field--full">
                <label className="prv-label">
                  Icon <span className="prv-optional">(Optional)</span>
                </label>

                {iconPreview ? (
                  <div className="prv-icon-preview">
                    <div className="prv-icon-preview-img-wrap">
                      {uploadingIcon && (
                        <div className="prv-icon-uploading-overlay">
                          <svg className="prv-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                          </svg>
                          <span>Uploading…</span>
                        </div>
                      )}
                      <img src={iconPreview} alt="Icon preview" className="prv-icon-preview-img" />
                    </div>
                    <div className="prv-icon-preview-info">
                      <span className="prv-icon-preview-label">Icon uploaded</span>
                      {vibeData.icon && (
                        <span className="prv-icon-preview-url">{vibeData.icon.slice(0, 48)}…</span>
                      )}
                      <div className="prv-icon-preview-actions">
                        <button
                          type="button"
                          className="prv-icon-change-btn"
                          onClick={() => iconInputRef.current?.click()}
                          disabled={uploadingIcon || loading}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Change
                        </button>
                        <button
                          type="button"
                          className="prv-icon-remove-btn"
                          onClick={removeIcon}
                          disabled={uploadingIcon || loading}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4h6v2" />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleIconFileChange}
                    />
                  </div>
                ) : (
                  <div
                    className={`prv-icon-dropzone ${iconDragOver ? "prv-icon-dropzone--over" : ""}`}
                    onClick={() => iconInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIconDragOver(true);
                    }}
                    onDragLeave={() => setIconDragOver(false)}
                    onDrop={handleIconDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && iconInputRef.current?.click()}
                  >
                    <div className="prv-icon-dropzone-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                    <div className="prv-icon-dropzone-text">
                      <span className="prv-icon-dropzone-title">
                        {iconDragOver ? "Drop image here" : "Upload icon image"}
                      </span>
                      <span className="prv-icon-dropzone-sub">
                        Click to browse or drag & drop · PNG, JPG, SVG, WebP
                      </span>
                    </div>
                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleIconFileChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="prv-footer">
            <div className="prv-footer-nav">
              <button
                type="button"
                className="prv-btn prv-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              {activeSection < sections.length - 1 ? (
                <button
                  type="button"
                  className="prv-btn prv-btn--primary"
                  onClick={() => setActiveSection((s) => Math.min(sections.length - 1, s + 1))}
                  disabled={loading}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  className="prv-btn prv-btn--submit"
                  disabled={loading || uploadingIcon}
                >
                  {loading ? (
                    "Saving…"
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Save Property Resale Vibe
                    </>
                  )}
                </button>
              )}
            </div>

            <Link to="/resale_vibe" className="prv-btn prv-btn--cancel" onClick={handleReset}>
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddPropertyResaleVibe;